import React, { useState, useMemo, useRef } from 'react';
import {
    Box, Grid, TextField, MenuItem, Typography
} from '@mui/material';
import {
    TransferWithinAStation as CancellationIcon
} from '@mui/icons-material';
import GenericPage from '../../Commun/components/GenericPage';
import PrintAgencyHeader from '../components/PrintAgencyHeader';
import { academyStore } from '../services/academyStore';
import { useLanguage } from '../../Commun/contexts/LanguageContext';
import { useAuth } from '../../Commun/contexts/AuthContext';
import { exportToExcel, exportToWord } from '../utils/printExportUtils';

export const PrintCancellationCertificatePage: React.FC = () => {
    const { language } = useLanguage();
    const { user } = useAuth();
    const isRtl = language === 'ar';

    const students = academyStore.getStudents();
    const groups = academyStore.getGroups();
    const groupStudents = academyStore.getGroupStudents();

    const todayStr = new Date().toISOString().slice(0, 10);
    const currentYear = new Date().getFullYear();
    const academicYear = `${currentYear}-${currentYear + 1}`;

    const [selectedStudentId, setSelectedStudentId] = useState<number | 'blank'>('blank');
    const [cancellationDate, setCancellationDate] = useState<string>(todayStr);
    const [reason, setReason] = useState<string>(language === 'ar' ? 'طلب الولي' : 'Demande des parents');
    const [feesPaid, setFeesPaid] = useState<boolean>(true);

    const printableRef = useRef<HTMLDivElement>(null);

    const selectedStudent = useMemo(() => {
        if (selectedStudentId === 'blank') return null;
        return students.find(s => s.id === selectedStudentId) || null;
    }, [students, selectedStudentId]);

    const studentGroup = useMemo(() => {
        if (!selectedStudent) return '';
        const gs = groupStudents.find(g => g.student_id === selectedStudent.id && g.status !== 'Dropped');
        if (gs) {
            const grp = groups.find(g => g.id === gs.group_id);
            if (grp) return grp.name;
        }
        return selectedStudent.school_level || '';
    }, [selectedStudent, groupStudents, groups]);

    const studentFullName = selectedStudent ? `${selectedStudent.last_name} ${selectedStudent.first_name}`.trim() : '—';
    const dob = selectedStudent?.date_of_birth || '—';
    const matricule = selectedStudent ? (selectedStudent.code || selectedStudent.STD_CODE || String(selectedStudent.id)) : '—';
    const schoolId = selectedStudent?.STD_UIDF ? selectedStudent.STD_UIDF.slice(0, 10) : '—';
    const classLabel = studentGroup || '—';
    const parentName = selectedStudent?.guardian_first_name ? `${selectedStudent.guardian_first_name} ${selectedStudent.guardian_last_name || ''}`.trim() : (selectedStudent?.emergency_contact || '—');
    const enrollmentDate = selectedStudent?.submitted_date || `${currentYear}-09-01`;

    // Export Handlers
    const handleExportExcel = () => {
        const headers = [
            language === 'ar' ? 'الحقل' : language === 'en' ? 'Field' : 'Champ',
            language === 'ar' ? 'القيمة' : language === 'en' ? 'Value' : 'Valeur'
        ];
        const rows = [
            [language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'Nom et prénom', studentFullName],
            [language === 'ar' ? 'تاريخ ومكان الميلاد' : language === 'en' ? 'Date & Place of birth' : 'Né(e) le et à', dob],
            [language === 'ar' ? 'الرقم الترتيبي' : language === 'en' ? 'Matricule' : 'N° matricule', matricule],
            [language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe', classLabel],
            [language === 'ar' ? 'الولي' : language === 'en' ? 'Parent' : 'Parent', parentName],
            [language === 'ar' ? 'تاريخ الدخول' : language === 'en' ? 'Enrollment Date' : 'Entré(e) le', enrollmentDate],
            [language === 'ar' ? 'تاريخ الشطب' : language === 'en' ? 'Withdrawal Date' : 'Radié(e) le', cancellationDate],
            [language === 'ar' ? 'السبب' : language === 'en' ? 'Reason' : 'Motif', reason]
        ];
        exportToExcel(`Certificat_Radiation_${matricule}`, 'Radiation', headers, rows);
    };

    const handleExportWord = () => {
        if (!printableRef.current) return;
        const title = language === 'ar' ? 'شهادة الشطب' : language === 'en' ? 'Cancellation Certificate' : 'Certificat de radiation';
        exportToWord(`Certificat_Radiation_${matricule}`, title, printableRef.current.innerHTML, isRtl);
    };

    const cellStyle = (width?: number, extra?: React.CSSProperties): React.CSSProperties => ({
        border: '1px solid #777',
        padding: '6px 8px',
        textAlign: isRtl ? 'right' : 'left',
        width,
        ...extra
    });

    const filters = (
        <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    select
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'اختيار' : language === 'en' ? 'Select' : 'Choisir'}
                    value={selectedStudentId}
                    onChange={e => setSelectedStudentId(e.target.value === 'blank' ? 'blank' : Number(e.target.value))}
                >
                    <MenuItem value="blank">
                        {language === 'ar' ? '— استمارة فارغة —' : language === 'en' ? '— Blank Certificate —' : '— Certificat vierge —'}
                    </MenuItem>
                    {students.map(s => (
                        <MenuItem key={s.id} value={s.id}>
                            {s.last_name} {s.first_name} {s.code ? `(${s.code})` : ''}
                        </MenuItem>
                    ))}
                </TextField>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
                <TextField
                    type="date"
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'تاريخ الشطب' : language === 'en' ? 'Cancellation Date' : 'Date de radiation'}
                    value={cancellationDate}
                    onChange={e => setCancellationDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                />
            </Grid>

            <Grid item xs={12} sm={12} md={5}>
                <TextField
                    fullWidth
                    size="small"
                    label={language === 'ar' ? 'السبب' : language === 'en' ? 'Reason' : 'Motif'}
                    value={reason}
                    onChange={e => setReason(e.target.value)}
                />
            </Grid>
        </Grid>
    );

    return (
        <GenericPage
            title={language === 'ar' ? 'شهادة الشطب (التحويل)' : language === 'en' ? 'Cancellation Certificate' : 'Certificat de radiation (transfert)'}
            icon={<CancellationIcon />}
            type="print"
            filters={filters}
            footerEnabled={false}
            onExportExcel={handleExportExcel}
            onExportWord={handleExportWord}
        >
            <div ref={printableRef} dir={isRtl ? 'rtl' : 'ltr'} style={{ maxWidth: 800, margin: '0 auto', color: '#111', fontFamily: 'Arial, sans-serif' }}>
                <PrintAgencyHeader
                    title={language === 'ar' ? 'شهادة الشطب' : language === 'en' ? 'CANCELLATION CERTIFICATE' : 'CERTIFICAT DE RADIATION'}
                    subtitle={`${language === 'ar' ? 'السنة الدراسية' : language === 'en' ? 'Academic Year' : 'Année scolaire'} ${academicYear}`}
                    showBorderBottom
                />

                <Box sx={{ my: 3 }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, marginBottom: 25 }}>
                        <tbody>
                            <tr>
                                <td style={cellStyle(220, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'اللقب والاسم' : language === 'en' ? 'Full Name' : 'Nom et prénom'}
                                </td>
                                <td style={cellStyle(undefined, { fontWeight: 600 })}>{studentFullName}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(220, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'تاريخ ومكان الميلاد' : language === 'en' ? 'Date & Place of birth' : 'Né(e) le et à'}
                                </td>
                                <td style={cellStyle()}>{dob}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(220, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'الرقم الترتيبي' : language === 'en' ? 'Matricule' : 'N° matricule'}
                                </td>
                                <td style={cellStyle()}>{matricule}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(220, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'رقم التعريف المدرسي' : language === 'en' ? 'School ID' : "N° d'identification scolaire"}
                                </td>
                                <td style={cellStyle()}>{schoolId}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(220, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'القسم' : language === 'en' ? 'Class' : 'Classe'}
                                </td>
                                <td style={cellStyle()}>{classLabel}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(220, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'الولي' : language === 'en' ? 'Parent' : 'Parent'}
                                </td>
                                <td style={cellStyle()}>{parentName}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(220, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'تاريخ الدخول' : language === 'en' ? 'Enrollment Date' : 'Entré(e) le'}
                                </td>
                                <td style={cellStyle()}>{enrollmentDate}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(220, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'تاريخ الشطب' : language === 'en' ? 'Cancellation Date' : 'Radié(e) le'}
                                </td>
                                <td style={cellStyle(undefined, { fontWeight: 600 })}>{cancellationDate}</td>
                            </tr>
                            <tr>
                                <td style={cellStyle(220, { fontWeight: 'bold', backgroundColor: '#f8fafc' })}>
                                    {language === 'ar' ? 'السبب' : language === 'en' ? 'Reason' : 'Motif'}
                                </td>
                                <td style={cellStyle()}>{reason}</td>
                            </tr>
                        </tbody>
                    </table>

                    {/* Attestation Body */}
                    <div style={{ fontSize: 13, lineHeight: 1.8, textAlign: 'justify', marginBottom: 25 }}>
                        {language === 'ar' ? (
                            'أشهد أنا مدير(ة) المؤسسة أن التلميذ(ة) أعلاه كان(ت) مسجلاً(ة) بصفة قانونية بهذه المؤسسة وأنه(ا) شُطب(ت) بتاريخ المذكور أعلاه بطلب من وليه(ا)، وسُلّمت له(ا) هذه الشهادة لتقديمها عند الاقتضاء، لا سيما قصد التسجيل في مؤسسة أخرى.'
                        ) : language === 'en' ? (
                            'I, the undersigned Director of the institution, certify that the above-named student was regularly enrolled in this institution and was withdrawn on the specified date at the request of their parent or guardian. This certificate is issued to serve for all legal intents and purposes, particularly with a view to enrollment in another school.'
                        ) : (
                            "Je soussigné(e), directeur(trice) de l'établissement, certifie que l'apprenant désigné(e) ci-dessus a été régulièrement inscrit(e) dans cet établissement et qu'il(elle) en est radié(e) à la date indiquée, à la demande de son parent ou tuteur. Le présent certificat lui est délivré pour servir et valoir ce que de droit, notamment en vue de son inscription dans un autre établissement."
                        )}
                    </div>

                    {/* Tuition situation */}
                    <div style={{ fontSize: 12, marginBottom: 35, padding: '8px 12px', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4 }}>
                        {language === 'ar' ? (
                            <span>وضعية المستحقات المدرسية : &nbsp; [✓] مسواة &nbsp;&nbsp;&nbsp; [ ] متبقي : ..................... دج</span>
                        ) : language === 'en' ? (
                            <span>Tuition Fee Status: &nbsp; [✓] Up to date &nbsp;&nbsp;&nbsp; [ ] Remaining due: ..................... DA</span>
                        ) : (
                            <span>Situation des frais de scolarité : &nbsp; [✓] à jour &nbsp;&nbsp;&nbsp; [ ] reste dû : ..................... DA</span>
                        )}
                    </div>

                    {/* Signatures */}
                    <table style={{ width: '100%', marginTop: 35, borderCollapse: 'collapse', fontSize: 12 }}>
                        <tbody>
                            <tr>
                                <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'right' : 'left' }}>
                                    <strong>{language === 'ar' ? 'الولي :' : language === 'en' ? 'Parent :' : 'Le parent :'}</strong>
                                    <div style={{ height: 60 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '60%' }} />
                                </td>
                                <td style={{ width: '50%', border: 'none', textAlign: isRtl ? 'left' : 'right' }}>
                                    <strong>{language === 'ar' ? 'المدير (الختم) :' : language === 'en' ? 'Director (Stamp) :' : 'Le directeur (cachet) :'}</strong>
                                    <div style={{ height: 60 }} />
                                    <div style={{ borderTop: '1px solid #999', width: '60%', marginInlineStart: 'auto' }} />
                                </td>
                            </tr>
                        </tbody>
                    </table>

                    <div style={{ marginTop: 35, textAlign: 'center', fontSize: 11, color: '#666' }}>
                        {language === 'ar' ? 'شكراً على ثقتكم.' : language === 'en' ? 'Thank you for your trust.' : 'Merci de votre confiance.'}
                    </div>
                </Box>
            </div>
        </GenericPage>
    );
};

export default PrintCancellationCertificatePage;
